/**
 * Carriageway clearance (issue #79): a single, shared, pure predicate that all
 * static scatter layers (roadside vegetation patches, woodland trees,
 * procedural rocks) apply at placement time so nothing plants in the middle
 * of a tarmac surface. "Too close to tarmac" means either:
 *
 *   - within (route ribbon half-width + margin) of the route polyline — the
 *     ribbon (routeRibbon.ts, RIBBON_WIDTH_M = 4 → 2 m half-width) is the only
 *     drawn tarmac, and trees/rocks on it read as broken; OR
 *   - within (a per-class road half-width + margin) of ANY nearby OSM road
 *     centreline of a drivable carriageway class.
 *
 * The route and the road centrelines are evaluated INDEPENDENTLY (the GPX track
 * and the OSM centreline of the "same" road are separate data sources and drift
 * apart): a candidate too close to either is dropped. The road test is the
 * minimum over ALL carriageway roads within reach, not just the road a patch was
 * offset from — so a plant clear of road A but sitting on a parallel/joining
 * road B is still dropped (junctions).
 *
 * Unpaved drivable ways (surface gravel/dirt/ground/unpaved) are treated the
 * same as tarmac: a plant in the middle of any drivable way looks equally wrong,
 * so clearance keys off the road CLASS (a carriageway or not), never the surface
 * tag. `service` roads (driveways / farm access / parking aisles) ARE cleared
 * since issue #138 draws every OSM road — including service tracks — as a
 * visible ribbon; a plant sitting on a rendered dirt track reads as broken just
 * like one on a lane. Service clearance uses the narrower service ribbon
 * half-width (SERVICE_WIDTH_M / 2). Note this differs from roadWalls/vegetation,
 * which still exclude service from WALLING/planting-along (a farm track has no
 * dry-stone walls) — clearance is only about keeping scatter OFF the rendered
 * surface.
 *
 * Pure and three.js-free (Node-testable): it only reads geometry and returns a
 * keep/drop decision. It has NO bearing on any LOS result (hard constraint #1 —
 * scene furniture only) and never touches elevation (hard constraint #6). Being
 * a per-candidate filter over already-seeded positions, dropping one candidate
 * never reshuffles any other (determinism preserved).
 *
 * Perf (hard rule, issue #17): built once per layer at construction/patch-
 * realisation time, never on the render loop. Roads are pre-clipped to the route
 * corridor (clipToCorridor) so far-away roads are never distance-tested per
 * candidate; a caller that already has its own route distance handy can reuse
 * it via the optional `routeDistM` argument to isClear() rather than
 * recomputing it.
 */
import type { LocalXZ } from '../core/coordinates';
import type { Road } from '../core/manifest';
import { densify, roadLocalLine } from './roadWalls';
import { clipToCorridor, distanceToPolylineM } from './corridor';

/** Route ribbon half-width (m). The ribbon rendered by routeRibbon.ts is
 * RIBBON_WIDTH_M (4 m) wide, so its footprint reaches 2 m either side of the
 * route centreline. Kept as a literal here to keep this module free of the
 * three.js-coupled routeRibbon import. */
export const ROUTE_HALF_WIDTH_M = 2;
/** Extra keep-off margin (m) added to every half-width so planting starts just
 * off the carriageway edge, not exactly on it. */
export const CLEARANCE_MARGIN_M = 1;
/** Total keep-off distance (m) from the route centreline. */
export const ROUTE_CLEARANCE_M = ROUTE_HALF_WIDTH_M + CLEARANCE_MARGIN_M;

/** Per-class carriageway half-width (m). Primary/secondary are the wider lanes;
 * tertiary/unclassified are single-track rural lanes; trunk (A-roads) is widest;
 * `service` uses the narrow service ribbon half-width (SERVICE_WIDTH_M 2.5 / 2)
 * since issue #138 renders service tracks as visible dirt ribbons. Kept as a
 * literal to keep this module free of the three.js-coupled roadRibbons import. */
export const ROAD_HALF_WIDTH_M: Record<string, number> = {
  trunk: 3.5,
  primary: 3,
  secondary: 3,
  tertiary: 2.5,
  unclassified: 2.5,
  service: 1.25,
};
/** Fallback half-width for an unrecognised (but included) carriageway class. */
const DEFAULT_ROAD_HALF_WIDTH_M = 2.5;

/** Road classes clearance is evaluated against. Every OSM road class is drawn as
 * a ribbon (issue #138), so every class — including `service` — keeps scatter
 * off its rendered surface. */
export const DEFAULT_CLEARANCE_ROAD_CLASSES = new Set<string>([
  'trunk',
  'primary',
  'secondary',
  'tertiary',
  'unclassified',
  'service',
]);

/** Roads are clipped to this reach (m) of the route corridor before any
 * per-candidate distance test — matches roadWalls' WALL_CORRIDOR_M so every
 * road a near-route candidate could sit on is included, and no others. */
export const DEFAULT_CLEARANCE_CORRIDOR_M = 300;

/** Spacing (m) roads are resampled to before corridor-clipping, so a road that
 * only dips into the corridor between two far-apart OSM vertices is still
 * detected (mirrors roadWalls' densify-before-clip). */
const CLEARANCE_DENSIFY_M = 12;

/** Total keep-off distance (m) from a road centreline of the given class. */
export function roadClearanceM(roadClass: string): number {
  return (ROAD_HALF_WIDTH_M[roadClass] ?? DEFAULT_ROAD_HALF_WIDTH_M) + CLEARANCE_MARGIN_M;
}

export interface TarmacClearanceOptions {
  /** OSM roads (data/config/roads.json). Missing/empty → no road clearance. */
  roads?: Road[] | null;
  /** Route polyline (local x/z). Missing/<2 points → no route clearance. */
  route?: LocalXZ[] | null;
  /** Carriageway classes to clear against (default DEFAULT_CLEARANCE_ROAD_CLASSES). */
  roadClasses?: Set<string>;
  /** Corridor (usually the route) roads are pre-clipped to; omit to test every
   * carriageway road unclipped (e.g. no-route degenerate case). */
  corridor?: LocalXZ[] | null;
  corridorMaxDistM?: number;
}

interface ClearanceRoad {
  line: LocalXZ[];
  clearanceM: number;
}

/**
 * Immutable clearance context built once per layer. `isClear(p)` returns true
 * when a candidate at p may be planted, false when it is too close to the route
 * or any in-reach carriageway.
 */
export class TarmacClearance {
  private readonly route: LocalXZ[] | null;
  private readonly roads: ClearanceRoad[];

  constructor(options: TarmacClearanceOptions = {}) {
    this.route = options.route && options.route.length >= 2 ? options.route : null;
    const classes = options.roadClasses ?? DEFAULT_CLEARANCE_ROAD_CLASSES;
    const corridor = options.corridor && options.corridor.length >= 2 ? options.corridor : null;
    const corridorMaxDistM = options.corridorMaxDistM ?? DEFAULT_CLEARANCE_CORRIDOR_M;

    const roads: ClearanceRoad[] = [];
    for (const road of options.roads ?? []) {
      if (!classes.has(road.road_class)) continue;
      const line = densify(roadLocalLine(road.polyline_bng), CLEARANCE_DENSIFY_M);
      if (line.length < 2) continue;
      const clearanceM = roadClearanceM(road.road_class);
      const runs = corridor ? clipToCorridor(line, corridor, corridorMaxDistM) : [line];
      for (const run of runs) roads.push({ line: run, clearanceM });
    }
    this.roads = roads;
  }

  /** True when a route polyline is being cleared against. */
  get hasRoute(): boolean {
    return this.route !== null;
  }

  /** Number of carriageway polylines (post-clip runs) being cleared against. */
  get roadCount(): number {
    return this.roads.length;
  }

  /**
   * True when a candidate at p is far enough from every tarmac surface to plant.
   *
   * `routeDistM`, when given, is the caller's already-computed distance from p
   * to the route polyline (a scatter layer with its own corridor filter can
   * reuse that) — passed in to avoid recomputing it. It must be the distance to
   * the SAME route polyline this context was built with; when omitted, the
   * distance is measured here.
   */
  isClear(p: LocalXZ, routeDistM?: number): boolean {
    if (this.route) {
      const dr = routeDistM ?? distanceToPolylineM(p, this.route);
      if (dr < ROUTE_CLEARANCE_M) return false;
    }
    for (const r of this.roads) {
      if (distanceToPolylineM(p, r.line) < r.clearanceM) return false;
    }
    return true;
  }
}
